import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Outlet } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { onAuthStateChanged } from 'firebase/auth'
import debounce from 'lodash.debounce'
import { linkDeviceToUser } from 'api/Retomagic/methods'
import PromoModal from 'components/modals/PromoModal'
import LogInModal from 'components/modals/LogInModal'
import SignUpModal from 'components/modals/SignUpModal'
import Toastify from 'components/helpers/Toastify'
import MainLayoutContext, {
  MainLayoutContextInterface,
  Stage,
} from 'contexts/MainLayoutContext'
import { useAppDispatch, useAppSelector } from 'hooks/store'
import useModal from 'hooks/useModal'
import { setUser } from 'store/reducers/authReducer'
import { BaseAsset } from 'types/Response'
import { auth } from 'utils/firebase'
import convertUserToUserInfo from 'utils/convertUserToUserInfo'
import MainLayoutHeader from './MainLayoutHeader'
import c from './MainLayout.module.scss'

function MainLayout() {
  const dispatch = useAppDispatch()

  const deviceId = useAppSelector((state) => state.settings.deviceId)
  const language = useAppSelector((state) => state.settings.language)

  const { i18n } = useTranslation()

  const {
    isOpen: isPromoModalOpen,
    open: openPromoModal,
    close: closePromoModal,
  } = useModal()

  const {
    isOpen: isLogInModalOpen,
    open: openLogInModal,
    close: closeLogInModal,
  } = useModal()

  const {
    isOpen: isSignUpModalOpen,
    open: openSignUpModal,
    close: closeSignUpModal,
  } = useModal()

  const [stage, setStage] = useState<Stage>('idle')
  const [processedAssets, setProcessedAssets] = useState<BaseAsset[]>([])
  const [currentAssetIndex, setCurrentAssetIndex] = useState<number>(0)

  const mainLayoutContextValue = useMemo<MainLayoutContextInterface>(() => {
    return {
      stage,
      setStage,
      processedAssets,
      setProcessedAssets,
      currentAssetIndex,
      setCurrentAssetIndex,
      openLogInModal,
      openSignUpModal,
    }
  }, [
    stage,
    processedAssets,
    currentAssetIndex,
    openLogInModal,
    openSignUpModal,
  ])

  const setHeight = useCallback(
    debounce(() => {
      const height = window.innerHeight
      document.documentElement.style.setProperty('--height', `${height}px`)
    }, 100),
    [],
  )

  useEffect(() => {
    setHeight()
    window.addEventListener('resize', setHeight)

    return () => {
      window.removeEventListener('resize', setHeight)
    }
  }, [])

  useEffect(() => {
    return onAuthStateChanged(auth, (currentUser) => {
      const userInfo = convertUserToUserInfo(currentUser)
      dispatch(setUser(userInfo))
      if (currentUser) {
        linkDeviceToUser().then().catch()
      }
    })
  }, [])

  useEffect(() => {
    const isShown = window.localStorage.getItem('neuro-art-promo-modal-shown')
    if (isShown) return
    window.localStorage.setItem('neuro-art-promo-modal-shown', 'true')
    openPromoModal()
  }, [openPromoModal])

  useEffect(() => {
    window.localStorage.setItem('lng', language)
    i18n.changeLanguage(language).then().catch()
  }, [language])

  useEffect(() => {
    window.localStorage.setItem('device-id', deviceId)
  }, [deviceId])

  return (
    <MainLayoutContext.Provider value={mainLayoutContextValue}>
      <div className={c.layout}>
        <MainLayoutHeader />
        <Outlet />
        <PromoModal open={isPromoModalOpen} onClose={closePromoModal} />
        <LogInModal open={isLogInModalOpen} onClose={closeLogInModal} />
        <SignUpModal open={isSignUpModalOpen} onClose={closeSignUpModal} />
        <Toastify />
      </div>
    </MainLayoutContext.Provider>
  )
}

export default MainLayout
